import { useEffect, useState } from 'react';
import api from '@/lib/api';
import MobileShell from '@/components/MobileShell';
import ResqlyCard from '@/components/resqly/ResqlyCard';
import BottomNav from '@/components/BottomNav';
import ProviderWithdrawalForm from '@/components/provider/ProviderWithdrawalForm';
import { Wallet } from 'lucide-react';

export default function ProviderEarnings() {
  const [data, setData] = useState({
    today: 0,
    total_available: 0,
    earnings_balance: 0,
    pending_withdrawal: 0,
    daily_history: [],
    payout_history: [],
    wallet: {},
  });
  const [entries, setEntries] = useState([]);
  const [slaMessage, setSlaMessage] = useState('');

  const load = () => {
    api.get('/providers/me/earnings-detail').then(({ data }) => {
      setData(data.summary || data);
      setEntries(data.entries || []);
    }).catch(() => {});
    api.get('/providers/me/plans').then(({ data }) => {
      setSlaMessage(data.payout_sla_message || '');
    }).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const wallet = data.wallet || {};

  return (
    <div className="resqly-shell">
      <div className="resqly-frame flex flex-col min-h-screen">
        <MobileShell title="Earnings" header>
          <div className="px-5 py-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <EarningStat label="Today's Earnings" value={`₹${data.today}`} testid="today-earnings" highlight />
              <EarningStat label="Total Available" value={`₹${data.total_available ?? 0}`} testid="total-available" highlight />
              <EarningStat label="Pending Withdrawal" value={`₹${data.pending_withdrawal ?? 0}`} testid="pending-withdrawal" />
              <EarningStat label="Recharge Balance" value={`₹${wallet.recharge_balance ?? 0}`} testid="recharge-balance" />
            </div>

            <ProviderWithdrawalForm
              available={data.total_available}
              slaMessage={slaMessage}
              onSuccess={load}
            />

            {entries.length > 0 && (
              <ResqlyCard className="p-4" elevated data-testid="earnings-breakdown">
                <h3 className="font-semibold text-sm mb-3">Earnings by booking</h3>
                {entries.map((e) => (
                  <div key={e.booking_id || e.order_id} className="border-b border-slate-100 py-2 text-sm last:border-0">
                    <div className="font-medium">{e.service_type || 'Service'} — {e.date}</div>
                    <div className="text-xs text-slate-500 grid grid-cols-2 gap-1 mt-1">
                      <span>Base: ₹{e.base_service}</span>
                      <span>Travel: ₹{e.travel}</span>
                      <span>Extra: ₹{e.extra_service}</span>
                      <span>Gross: ₹{e.gross_provider_amount}</span>
                      {e.commission > 0 && <span>Commission: ₹{e.commission}</span>}
                      {e.penalty > 0 && <span className="text-red-600">Penalty: ₹{e.penalty}</span>}
                      <span className="font-semibold">Net: ₹{e.net_earnings}</span>
                    </div>
                  </div>
                ))}
              </ResqlyCard>
            )}

            {data.daily_history?.length > 0 && (
              <ResqlyCard className="p-4" elevated>
                <h3 className="font-semibold text-sm">Last 30 days</h3>
                {data.daily_history.slice(0, 30).map((row) => (
                  <div key={row.date} className="flex justify-between text-sm mt-2">
                    <span>{row.date}</span><span className="font-medium">₹{row.amount}</span>
                  </div>
                ))}
              </ResqlyCard>
            )}

            <ResqlyCard className="p-4" elevated>
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <Wallet className="w-4 h-4 text-blue-700" /> Payout history
              </h3>
              {data.payout_history?.length === 0 ? (
                <p className="text-sm text-slate-500 mt-2">No payouts yet.</p>
              ) : (
                data.payout_history.map((p) => (
                  <div key={p.id} className="flex justify-between text-sm mt-2">
                    <span>{p.date}</span><span>₹{p.amount} ({p.status})</span>
                  </div>
                ))
              )}
            </ResqlyCard>
          </div>
        </MobileShell>
        <BottomNav variant="provider" />
      </div>
    </div>
  );
}

function EarningStat({ label, value, testid, highlight = false }) {
  return (
    <ResqlyCard
      className={`p-3 ${highlight ? 'bg-gradient-to-br from-blue-50 to-white border-blue-100' : ''}`}
      data-testid={testid}
      elevated={highlight}
    >
      <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">{label}</div>
      <div className={`mt-1 font-bold tracking-tight ${highlight ? 'text-xl text-blue-900' : 'text-lg text-slate-900'}`}>
        {value}
      </div>
    </ResqlyCard>
  );
}
